import { type IconDefinition } from '@fortawesome/fontawesome-svg-core';
import {
  faArrowUpRightFromSquare,
  faCheck,
  faChevronDown,
  faPlus,
  faTimesCircle
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Combobox,
  ComboboxButton,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions
} from '@headlessui/react';
import _debounce from 'lodash/debounce';
import React, { type JSX, type ReactNode, useId, useMemo, useRef, useState } from 'react';

import { OptionType } from '@/ui/components/Form/Select/types';

import { Avatar } from '../Avatar';
import { cn } from '../lib/utils';

const MAX_RENDERED_OPTIONS = 300;

const HUICombobox = Combobox as React.ComponentType<{
  as?: React.ElementType;
  value?: unknown;
  onChange?: (value: unknown) => void;
  disabled?: boolean;
  multiple?: boolean;
  immediate?: boolean;
  by?: string;
  className?: string;
  children?: React.ReactNode;
}>;

type SelectSingleProps = {
  multiple?: false;
  selectedOption?: OptionType<string> | null;
  onChange: (value: OptionType<string> | null) => void;
  clearable?: boolean;
  appendedText?: string;
  menuPlacement?: 'bottom' | 'top';
  leftSection?: ReactNode;
};

type SelectMultiProps = {
  multiple: true;
  selectedOptions?: OptionType<string>[];
  onChange: (values: OptionType<string>[]) => void;
  // When true, typing a value that doesn't match any existing option shows an
  // "Add <value>" choice, letting the user select a custom value that isn't
  // one of the predefined `options`.
  creatable?: boolean;
  isCompact?: boolean;
};

type SelectSharedProps = {
  ariaLabel?: string;
  autoFocus?: boolean;
  className?: string;
  debounce?: boolean;
  disabled?: boolean;
  error?: string;
  helpText?: string;
  label?: string | JSX.Element;
  leftIcon?: IconDefinition | null;
  missingOptionsText?: string;
  onSearch?: (query: string) => void;
  options: OptionType<string>[];
  placeholder?: string;
  showLabel?: boolean;
};

export type SelectProps = (SelectSingleProps | SelectMultiProps) & SelectSharedProps;

function Select(props: SelectProps) {
  const {
    ariaLabel,
    autoFocus,
    className,
    debounce = true,
    disabled = false,
    error,
    helpText,
    label,
    leftIcon,
    missingOptionsText = 'No options',
    multiple,
    onSearch,
    options,
    placeholder = '',
    showLabel = true
  } = props;

  // A label that isn't a plain string (e.g. one with a required asterisk)
  // still needs an id to point its <label htmlFor> at the input.
  const generatedId = useId();
  const inputId =
    label && typeof label === 'string'
      ? `select-${label.replace(/\s+/g, '-').toLowerCase()}`
      : label
        ? generatedId
        : undefined;

  const [query, setQuery] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const isMulti = multiple === true;
  const multiSelectedOptions = (props as SelectMultiProps).selectedOptions;
  const selectedOptions = useMemo<OptionType<string>[]>(
    () => (isMulti ? (multiSelectedOptions ?? []) : []),
    [isMulti, multiSelectedOptions]
  );
  const selectedOption: OptionType<string> | null = !isMulti
    ? ((props as SelectSingleProps).selectedOption ?? null)
    : null;
  const leftSection = !isMulti ? (props as SelectSingleProps).leftSection : undefined;

  const clearable = !isMulti ? ((props as SelectSingleProps).clearable ?? true) : false;
  const appendedText = !isMulti ? (props as SelectSingleProps).appendedText : undefined;
  const menuPlacement = !isMulti
    ? ((props as SelectSingleProps).menuPlacement ?? 'bottom')
    : 'bottom';
  const creatable = isMulti ? ((props as SelectMultiProps).creatable ?? false) : false;
  const isCompact = isMulti ? ((props as SelectMultiProps).isCompact ?? false) : false;
  const tokensRef = useRef<HTMLDivElement>(null);

  const revealInput = () => {
    if (isCompact && tokensRef.current) {
      tokensRef.current.scrollLeft = tokensRef.current.scrollWidth;
    }
  };

  const selectedValues = useMemo(
    () => new Set(selectedOptions.map((o) => o.value)),
    [selectedOptions]
  );
  const selectedValuesLower = useMemo(
    () => new Set(selectedOptions.map((o) => o.value.toLowerCase())),
    [selectedOptions]
  );

  const filteredOptions = useMemo(() => {
    const unpicked = options.filter((o) => !selectedValues.has(o.value));
    const base = !isMulti ? options : isCompact ? [...selectedOptions, ...unpicked] : unpicked;
    return query === ''
      ? base
      : base.filter((o) => o.label.toLowerCase().includes(query.toLowerCase()));
  }, [options, query, selectedValues, selectedOptions, isMulti, isCompact]);

  const trimmedQuery = query.trim();
  const createOption: OptionType<string> | null = useMemo(() => {
    if (!creatable || trimmedQuery === '') return null;
    const alreadyExists =
      selectedValuesLower.has(trimmedQuery.toLowerCase()) ||
      options.some((o) => o.value.toLowerCase() === trimmedQuery.toLowerCase());
    return alreadyExists ? null : { value: trimmedQuery, label: trimmedQuery };
  }, [creatable, trimmedQuery, options, selectedValuesLower]);

  const renderedOptions = useMemo(
    () => filteredOptions.slice(0, MAX_RENDERED_OPTIONS),
    [filteredOptions]
  );
  const hiddenCount = filteredOptions.length - renderedOptions.length;

  const debounceSearch = useMemo(
    () => _debounce((v: string) => onSearch?.(v), 500, { leading: false, trailing: true }),
    [onSearch]
  );

  const handleSearch = (value: string) => {
    setQuery(value);
    if (debounce) debounceSearch(value);
    else onSearch?.(value);
  };

  const handleSingleChange = (value: unknown) => {
    setQuery('');
    (props as SelectSingleProps).onChange(value as OptionType<string> | null);
  };

  const handleMultiChange = (next: unknown) => {
    setQuery('');
    (props as SelectMultiProps).onChange(next as OptionType<string>[]);
    setTimeout(() => {
      inputRef.current?.focus();
      revealInput();
    }, 0);
  };

  const removeOption = (value: string) => {
    (props as SelectMultiProps).onChange(selectedOptions.filter((o) => o.value !== value));
  };

  const showClear = clearable && !!selectedOption?.value;
  const showOutlink = !!selectedOption?.outlinkUrl;
  const hasError = !!error;

  const wrapperCls = cn(
    'rounded-lg border transition-shadow',
    'border-uniform-gray-300 bg-white dark:border-uniform-gray-600 dark:bg-uniform-gray-900',
    'focus-within:ring-2 focus-within:border-transparent focus-within:ring-blue-500 dark:focus-within:ring-blue-400',
    hasError &&
      'border-red-400 dark:border-red-500 focus-within:ring-red-400 dark:focus-within:ring-red-500'
  );

  const dropdownCls = cn(
    // Grow to fit the widest option instead of clamping to the trigger's
    // width (which cuts off longer labels) -- still at least as wide as the
    // trigger, and capped so a pathologically long label can't blow out the
    // layout; overflow-x-auto is the fallback for anything that still
    // exceeds the cap.
    'z-50 w-max min-w-[var(--input-width)] max-w-[min(32rem,90vw)] overflow-auto rounded-lg border py-1 [--anchor-gap:4px]',
    'border-uniform-gray-200 bg-white shadow-lg dark:border-uniform-gray-700 dark:bg-uniform-gray-900 empty:hidden'
  );

  const optionCls = cn(
    'relative flex cursor-pointer select-none items-center gap-2 py-2 pl-8 pr-3 text-sm',
    'text-uniform-gray-700 dark:text-uniform-gray-300',
    'data-[focus]:bg-uniform-gray-100 data-[focus]:text-uniform-gray-900',
    'dark:data-[focus]:bg-uniform-gray-800 dark:data-[focus]:text-uniform-gray-100'
  );

  const chevronBtnCls = cn(
    'group flex shrink-0 items-center justify-center self-stretch border-l px-2',
    'border-uniform-gray-200 dark:border-uniform-gray-700',
    'text-uniform-gray-400 hover:text-uniform-gray-600 dark:text-uniform-gray-500 dark:hover:text-uniform-gray-300',
    '[outline:none] transition-colors'
  );

  const renderOptions = (anchor: string) => (
    <ComboboxOptions
      anchor={anchor as Parameters<typeof ComboboxOptions>[0]['anchor']}
      className={dropdownCls}>
      {filteredOptions.length === 0 && !createOption ? (
        isMulti ? (
          <div className="px-3 py-2 text-sm text-uniform-gray-500 dark:text-uniform-gray-400">
            {missingOptionsText}
          </div>
        ) : (
          <ComboboxOption className={optionCls} value={null} disabled>
            <span className="text-uniform-gray-400 dark:text-uniform-gray-500">
              {missingOptionsText}
            </span>
          </ComboboxOption>
        )
      ) : (
        [
          createOption ? (
            <ComboboxOption
              key={`__create-${createOption.value}`}
              className={optionCls}
              value={createOption}>
              <div className="flex items-center gap-2">
                <FontAwesomeIcon
                  icon={faPlus}
                  className="h-3.5 w-3.5 shrink-0 text-uniform-gray-500 dark:text-uniform-gray-400"
                />
                <span className="block truncate">Add &ldquo;{createOption.label}&rdquo;</span>
              </div>
            </ComboboxOption>
          ) : null,
          ...renderedOptions.map((option) => (
            <ComboboxOption
              key={option?.value?.toString() ?? option.label}
              className={optionCls}
              value={option}>
              {({ selected, focus }) => (
                <>
                  <div className="flex items-center gap-2">
                    {option.icon ? (
                      <FontAwesomeIcon
                        icon={option.icon}
                        className="h-4 w-4 shrink-0 text-uniform-gray-500 dark:text-uniform-gray-400"
                      />
                    ) : option.avatar !== undefined ? (
                      React.isValidElement(option.avatar) ? (
                        option.avatar
                      ) : (
                        <Avatar
                          src={typeof option.avatar === 'string' ? option.avatar : undefined}
                          name={option.label}
                          size="sm"
                        />
                      )
                    ) : null}
                    <span className={cn('block truncate', selected && 'font-semibold')}>
                      {option.label}
                    </span>
                    {option.subLabel && (
                      <span
                        className={cn(
                          'block text-xs',
                          focus
                            ? 'text-uniform-gray-600 dark:text-uniform-gray-400'
                            : 'text-uniform-gray-600 dark:text-uniform-gray-400'
                        )}>
                        — {option.subLabel}
                      </span>
                    )}
                    {!isMulti && option.outlinkUrl && (
                      <a
                        aria-label={`Open ${option.label} in new tab`}
                        className="ml-auto text-uniform-gray-400 hover:text-uniform-gray-600 dark:text-uniform-gray-500 dark:hover:text-uniform-gray-300"
                        href={option.outlinkUrl}
                        onClick={(e) => e.stopPropagation()}
                        onMouseDown={(e) => e.stopPropagation()}
                        rel="noopener noreferrer"
                        target="_blank"
                        title="Open in new tab">
                        <FontAwesomeIcon icon={faArrowUpRightFromSquare} className="h-3 w-3" />
                      </a>
                    )}
                  </div>
                  {selected && (
                    <span className="absolute left-1.5 flex items-center text-uniform-blue-700 dark:text-uniform-blue-300">
                      <FontAwesomeIcon icon={faCheck} className="h-3.5 w-3.5" />
                    </span>
                  )}
                </>
              )}
            </ComboboxOption>
          )),
          hiddenCount > 0 ? (
            <div
              key="__more"
              className="pointer-events-none select-none px-3 py-2 text-xs text-uniform-gray-500 dark:text-uniform-gray-400">
              {hiddenCount} more — type to filter
            </div>
          ) : null
        ]
      )}
    </ComboboxOptions>
  );

  return (
    <div className={cn('flex flex-col gap-1', className)}>
      {showLabel && label && (
        <label
          htmlFor={inputId}
          className="block text-sm font-medium text-uniform-gray-700 dark:text-uniform-gray-300">
          {label}
        </label>
      )}
      {helpText && (
        <p className="text-sm text-uniform-gray-600 dark:text-uniform-gray-400">{helpText}</p>
      )}

      <div className={appendedText ? 'flex items-center gap-2' : undefined}>
        <HUICombobox
          as="div"
          className="relative"
          disabled={disabled}
          immediate
          by={isCompact ? 'value' : undefined}
          multiple={isMulti || undefined}
          value={isMulti ? selectedOptions : selectedOption}
          onChange={isMulti ? handleMultiChange : handleSingleChange}>
          {isMulti ? (
            <div
              className={cn(
                wrapperCls,
                'flex items-stretch cursor-text px-2 py-1.5',
                disabled && 'opacity-50 cursor-not-allowed'
              )}
              onClick={() => !disabled && inputRef.current?.focus()}>
              {leftIcon && (
                <FontAwesomeIcon
                  icon={leftIcon}
                  className="pointer-events-none self-center mr-1.5 h-4 w-4 shrink-0 text-uniform-gray-400 dark:text-uniform-gray-500"
                  aria-hidden
                />
              )}
              <div
                ref={tokensRef}
                className={cn(
                  'flex min-w-0 flex-1 items-center gap-x-1 gap-y-1 py-0.5',
                  isCompact ? 'flex-nowrap overflow-x-auto [scrollbar-width:thin]' : 'flex-wrap'
                )}>
                {selectedOptions.map((option) => (
                  <span
                    key={option.value}
                    className={cn(
                      'inline-flex items-center gap-1 rounded px-2 py-0.5 text-xs font-medium bg-uniform-blue-50 text-uniform-blue-900 dark:bg-uniform-blue-900/40 dark:text-uniform-blue-100',
                      isCompact && 'max-w-64 shrink-0 whitespace-nowrap'
                    )}
                    data-testid={`token-${option.value}`}>
                    {isCompact ? (
                      <span className="truncate" title={option.label}>
                        {option.label}
                      </span>
                    ) : (
                      option.label
                    )}
                    {!disabled && (
                      <button
                        aria-label={`Remove ${option.label}`}
                        className="ml-0.5 text-uniform-gray-400 hover:text-uniform-gray-700 focus:outline-none dark:hover:text-uniform-gray-200"
                        onClick={(e) => {
                          e.stopPropagation();
                          removeOption(option.value);
                        }}
                        type="button">
                        <FontAwesomeIcon icon={faTimesCircle} className="h-3 w-3" aria-hidden />
                      </button>
                    )}
                  </span>
                ))}
                <ComboboxInput
                  ref={inputRef}
                  aria-label={ariaLabel}
                  autoFocus={autoFocus}
                  className={cn(
                    'min-w-[120px] flex-1 border-0 bg-transparent p-0 text-sm [outline:none] focus:ring-0',
                    'text-uniform-gray-900 dark:text-uniform-gray-100',
                    'placeholder:text-uniform-gray-400 dark:placeholder:text-uniform-gray-500',
                    disabled && 'cursor-not-allowed'
                  )}
                  displayValue={() => query}
                  onChange={(e) => handleSearch(e.target.value)}
                  onFocus={revealInput}
                  placeholder={selectedOptions.length === 0 ? placeholder : ''}
                />
              </div>
              {isCompact && selectedOptions.length > 0 && !disabled && (
                <button
                  aria-label={`Clear ${ariaLabel ?? 'all'}`}
                  className="flex shrink-0 items-center px-1.5 text-uniform-gray-400 hover:text-uniform-gray-600 dark:text-uniform-gray-500 dark:hover:text-uniform-gray-300"
                  onClick={(e) => {
                    e.stopPropagation();
                    (props as SelectMultiProps).onChange([]);
                  }}
                  type="button">
                  <FontAwesomeIcon icon={faTimesCircle} className="h-3.5 w-3.5" aria-hidden />
                </button>
              )}
              <ComboboxButton className="group flex items-center justify-center border-l pl-1.5 border-uniform-gray-200 dark:border-uniform-gray-700 text-uniform-gray-400 dark:text-uniform-gray-500 hover:text-uniform-gray-600 dark:hover:text-uniform-gray-300 [outline:none] transition-colors">
                <FontAwesomeIcon
                  icon={faChevronDown}
                  className="h-full w-full transition-transform duration-200 group-data-[open]:rotate-180"
                  aria-hidden
                />
              </ComboboxButton>
              {renderOptions('bottom')}
            </div>
          ) : (
            <div
              className={cn(
                wrapperCls,
                'flex items-center overflow-hidden',
                disabled && 'opacity-50 cursor-not-allowed'
              )}>
              {leftSection ? (
                <span className="pointer-events-none absolute left-2.5 shrink-0" aria-hidden>
                  {leftSection}
                </span>
              ) : (
                leftIcon && (
                  <FontAwesomeIcon
                    icon={leftIcon}
                    className="pointer-events-none absolute left-3 h-4 w-4 shrink-0 text-uniform-gray-400 dark:text-uniform-gray-500"
                    aria-hidden
                  />
                )
              )}
              <ComboboxInput
                id={inputId}
                aria-label={ariaLabel}
                autoFocus={autoFocus}
                className={cn(
                  'w-full bg-transparent py-2 text-sm [outline:none] border-none',
                  leftSection ? 'pl-10' : leftIcon ? 'pl-9' : 'pl-3',
                  'text-uniform-gray-900 dark:text-uniform-gray-100',
                  'placeholder:text-uniform-gray-400 dark:placeholder:text-uniform-gray-500',
                  disabled && 'cursor-not-allowed'
                )}
                onChange={(e) => handleSearch(e.target.value)}
                placeholder={placeholder}
                displayValue={(o: unknown) => (o as OptionType | null)?.label ?? ''}
              />
              {showOutlink && (
                <a
                  aria-label="Open in new tab"
                  className="flex shrink-0 items-center px-2 text-uniform-gray-400 hover:text-uniform-gray-600 dark:text-uniform-gray-500 dark:hover:text-uniform-gray-300"
                  href={selectedOption!.outlinkUrl}
                  onClick={(e) => e.stopPropagation()}
                  onMouseDown={(e) => e.stopPropagation()}
                  rel="noopener noreferrer"
                  target="_blank"
                  title="Open in new tab">
                  <FontAwesomeIcon icon={faArrowUpRightFromSquare} className="h-3.5 w-3.5" />
                </a>
              )}
              {showClear && (
                <button
                  aria-label="Clear"
                  className="flex shrink-0 items-center px-2 text-uniform-gray-400 hover:text-uniform-gray-600 dark:text-uniform-gray-500 dark:hover:text-uniform-gray-300"
                  disabled={disabled}
                  onClick={() => handleSingleChange(null)}
                  type="button">
                  <FontAwesomeIcon icon={faTimesCircle} className="h-3.5 w-3.5" />
                </button>
              )}
              <ComboboxButton className={chevronBtnCls}>
                <FontAwesomeIcon
                  icon={faChevronDown}
                  className="h-3 w-3 transition-transform duration-200 group-data-[open]:rotate-180"
                />
              </ComboboxButton>
              {renderOptions(menuPlacement === 'top' ? 'top' : 'bottom')}
            </div>
          )}
        </HUICombobox>

        {appendedText && (
          <span className="text-sm text-uniform-gray-600 dark:text-uniform-gray-400">
            {appendedText}
          </span>
        )}
      </div>

      {error && <p className="text-sm text-red-500 dark:text-red-400">{error}</p>}
    </div>
  );
}

export { Select };
